/**
 * BootScreen Component
 *
 * Displays service status during cold starts while backend services wake up.
 * Shows professional Spanish messaging with per-service status indicators.
 *
 * Phase 58: Scale-to-Zero with Boot Screen
 */

import React from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ErrorIcon from '@mui/icons-material/Error'

import { ServiceStatus, ServiceStatusType } from '@/hooks/useReadiness'
import { getServiceDisplayName } from '@/config/routeServices'

interface BootScreenProps {
  services: Record<string, ServiceStatus>
  estimatedSeconds: number
  username?: string
  isTimedOut?: boolean
}

/**
 * Get status icon for a service
 */
function getStatusIcon(status: ServiceStatusType): React.ReactNode {
  switch (status) {
    case 'ready':
    case 'disabled':
      return <CheckCircleIcon color="success" />
    case 'error':
      return <ErrorIcon color="error" />
    case 'starting':
    default:
      return <RadioButtonUncheckedIcon color="action" />
  }
}

/**
 * Get status message for a service
 */
function getStatusMessage(status: ServiceStatus): string {
  switch (status.status) {
    case 'ready':
      return status.latency_ms
        ? `Conectado (${status.latency_ms}ms)`
        : 'Conectado'
    case 'disabled':
      return 'No requerido'
    case 'error':
      return status.error || 'Error de conexion'
    case 'starting':
      return status.message || 'Iniciando...'
    default:
      return 'Verificando...'
  }
}

/**
 * Get greeting based on time of day (Spanish)
 */
function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Buenos dias'
  if (hour < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

const BootScreen: React.FC<BootScreenProps> = ({
  services,
  estimatedSeconds,
  username,
  isTimedOut = false,
}) => {
  const greeting = getGreeting()
  const serviceEntries = Object.entries(services)

  return (
    <Box
      data-testid="boot-screen"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Greeting */}
        <Typography
          variant="h4"
          component="h1"
          sx={{ mb: 1, fontWeight: 500 }}
        >
          {greeting}{username ? `, ${username}` : ''}
        </Typography>

        {/* Status message */}
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          {isTimedOut
            ? 'La conexion esta tardando mas de lo esperado'
            : 'La plataforma esta despertando...'}
        </Typography>

        {/* Service status list */}
        {serviceEntries.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <List
              aria-live="polite"
              aria-label="Estado de los servicios"
              sx={{ py: 1 }}
            >
              {serviceEntries.map(([serviceName, status]) => (
                <ListItem key={serviceName} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getStatusIcon(status.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={getServiceDisplayName(serviceName)}
                    secondary={getStatusMessage(status)}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: status.status === 'error' ? 'error' : 'text.secondary',
                    }}
                  />
                  {status.status === 'starting' && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Estimated time */}
        {!isTimedOut && estimatedSeconds > 0 && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Tiempo estimado: ~{estimatedSeconds} segundos
          </Typography>
        )}

        {/* Explanation message */}
        <Typography
          variant="body"
          color="text.secondary"
          sx={{
            maxWidth: 360,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          {isTimedOut
            ? 'Puedes intentar recargar la pagina o esperar un poco mas.'
            : 'La plataforma se pone en modo ahorro cuando no hay actividad. Esto nos ayuda a ser sostenibles.'}
        </Typography>
      </Box>
    </Box>
  )
}

export default BootScreen
